import React, { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import "bootstrap/dist/css/bootstrap.min.css";
import "./CalendarDesktop.css";
import "../global.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faAnglesRight, faAnglesLeft
} from '@fortawesome/free-solid-svg-icons';

const CalendarDesktop = () => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedTab, setSelectedTab] = useState("month");
  const [comments, setComments] = useState({});
  const [modalData, setModalData] = useState({
    isOpen: false,
    date: null,
    text: "",
    index: null,
  });
  const [selectedYear, setSelectedYear] = useState(currentDate.year());

  const modalRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closePopup();
      }
    };
    if (modalData.isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [modalData.isOpen]);

  const closePopup = () => setModalData({ isOpen: false, date: null, text: "", index: null });

  const handleAddComment = (date) => {
    setModalData({ isOpen: true, date, text: "", index: null });
  };

  const handleEditComment = (date, index) => {
    setModalData({ isOpen: true, date, text: comments[date][index], index });
  };

  const handleSaveComment = () => {
    setComments((prev) => {
      const updatedComments = prev[modalData.date] ? [...prev[modalData.date]] : [];
      if (modalData.index !== null) {
        updatedComments[modalData.index] = modalData.text;
      } else {
        updatedComments.push(modalData.text);
      }
      return { ...prev, [modalData.date]: updatedComments };
    });
    closePopup();
  };

  const handleDeleteComment = (date, index) => {
    setComments((prev) => {
      const updatedComments = [...prev[date]];
      updatedComments.splice(index, 1);
      return { ...prev, [date]: updatedComments };
    });
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value, 10);
    setSelectedYear(year);
    setCurrentDate(currentDate.year(year));
  };

  const goToPreviousYear = () => {
    const newYear = selectedYear - 1;
    setSelectedYear(newYear);
    setCurrentDate(currentDate.year(newYear));
  };

  const goToNextYear = () => {
    const newYear = selectedYear + 1;
    setSelectedYear(newYear);
    setCurrentDate(currentDate.year(newYear));
  };

  const renderMonthView = () => {
    const startOfMonth = currentDate.startOf("month");
    const endOfMonth = currentDate.endOf("month");
    let currentDay = startOfMonth.startOf("week");
    const dates = [];

    while (currentDay.isBefore(endOfMonth.endOf("week"))) {
      dates.push(currentDay);
      currentDay = currentDay.add(1, "day");
    }

    return (
      <table className="table table-bordered calendar-table">
        <thead>
          <tr>
            {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
              (day) => (
                <th key={day}>{day}</th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: Math.ceil(dates.length / 7) }, (_, rowIndex) => (
            <tr key={rowIndex}>
              {dates.slice(rowIndex * 7, rowIndex * 7 + 7).map((date) => {
                const dateString = date.format("YYYY-MM-DD");
                const isToday = date.isSame(dayjs(), "day");
                return (
                  <td
                    key={dateString}
                    className={`calendar-cell ${date.month() !== currentDate.month() ? "td-bg-light text-muted" : ""} ${isToday ? "today-highlight" : ""}`}
                    onClick={() => handleAddComment(dateString)}
                  >
                    {date.date()}
                    {comments[dateString] && (
                      <div className="comment-container">
                        {comments[dateString].map((comment, index) => (
                          <span
                            key={index}
                            className="comment-badge"
                            title={comment}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditComment(dateString, index);
                            }}
                          >
                            {comment}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = currentDate.startOf("week");
    const dates = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, "day"));

    return (
      <table className="table table-bordered calendar-table">
        <thead>
          <tr>
            <th>Time</th>
            {dates.map((date) => (
              <th key={date.format("YYYY-MM-DD")}>{date.format("ddd, MMM D")}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 24 }, (_, hour) => (
            <tr key={hour}>
              <td>{`${hour}:00`}</td>
              {dates.map((date) => {
                const dateString = `${date.format("YYYY-MM-DD")}-${hour}`; // Include hour in dateString
                return (
                  <td
                    key={dateString}
                    className="calendar-cell"
                    onClick={() => handleAddComment(dateString)}
                  >
                    {comments[dateString] && (
                      <div className="comment-container">
                        {comments[dateString].map((comment, index) => (
                          <span
                            key={index}
                            className="comment-badge"
                            title={comment}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditComment(dateString, index);
                            }}
                          >
                            {comment}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderDayView = () => {
    const date = currentDate;
    const dateStringBase = date.format("YYYY-MM-DD");

    return (
      <table className="table table-bordered calendar-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>{date.format("ddd, MMM D")}</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 24 }, (_, hour) => {
            const dateString = `${dateStringBase}-${hour}`; // Include hour in dateString
            return (
              <tr key={hour}>
                <td>{`${hour}:00`}</td>
                <td
                  className="calendar-cell"
                  onClick={() => handleAddComment(dateString)}
                >
                  {comments[dateString] && (
                    <div className="comment-container">
                      {comments[dateString].map((comment, index) => (
                        <span
                          key={index}
                          className="comment-badge"
                          title={comment}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditComment(dateString, index);
                          }}
                        >
                          {comment}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderView = () => {
    switch (selectedTab) {
      case "month":
        return renderMonthView();
      case "week":
        return renderWeekView();
      case "day":
        return renderDayView();
      default:
        return null;
    }
  };

  // Generate an array of years for the dropdown
  const years = Array.from({ length: 50 }, (_, i) => currentDate.year() - 25 + i);

  return (
    <div className="calendar-container row">
      <div className="col-lg-3">
        <button className="btn bg-gray btn-sm text-dark" onClick={() => setCurrentDate(currentDate.subtract(1, selectedTab))}>
          <FontAwesomeIcon icon={faAnglesLeft} className="f-10 prev" />
        </button>
        <button className="btn bg-gray btn-sm ml-10 text-dark" onClick={() => setCurrentDate(currentDate.add(1, selectedTab))}>
          <FontAwesomeIcon icon={faAnglesRight} className="f-10 nxt" />
        </button>
      </div>
      <div className="col-lg-6 justify-content-between align-items-center mb-3">
        <div className="d-flex justify-content-center align-items-center gap-10">
          <h5 className="m-0">{currentDate.format("MMMM YYYY")}</h5>
          <div className="year-navigation">
            <button className="btn btn-sm btn-secondary" onClick={goToPreviousYear}>
              ◄
            </button>
            <select
              className="form-select m-l-5"
              value={selectedYear}
              onChange={handleYearChange}
              style={{ width: "100px" }}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <button className="btn btn-sm btn-secondary" onClick={goToNextYear}>
              ►
            </button>
          </div>
        </div>
        
      </div>
      
      <div className="col-lg-3 mb-3 text-end">
        {["month", "week", "day"].map((tab) => (
          <button
            key={tab}
            className={`btn btn-sm ${selectedTab === tab ? "btn-success" : "btn-info"} me-2`}
            onClick={() => setSelectedTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      
      <div className="w-100 mt-2">{renderView()}</div>

      {modalData.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content" ref={modalRef}>
            <h5 className="text-dark">{modalData.index !== null ? "Edit" : "Add"} Comment for {modalData.date}</h5>
            <textarea
              className="form-control text-dark"
              rows="3"
              value={modalData.text}
              onChange={(e) => setModalData({ ...modalData, text: e.target.value })}
            />
            <div className="d-flex justify-content-end mt-3">
              <button className="btn btn-secondary btn-sm me-2" onClick={closePopup}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSaveComment}>
                Save
              </button>
            </div>
            {comments[modalData.date] && (
              <ul className="list-group mt-3">
                {comments[modalData.date].map((comment, index) => (
                  <li key={index} className="list-group-item d-flex justify-content-between">
                    {comment}
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteComment(modalData.date, index)}>
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarDesktop;